import { Link } from 'react-router-dom';
import {
    attentionActionLabel,
    attentionHref,
    CPM_ATTENTION_TYPE,
    formatCpmCount,
} from '../../../constants/centralProductMaster.constants';
import type { CpmNeedsAttentionItem } from '../../../types/centralProductMaster.types';

type Tone = {
    bg: string;
    border: string;
    icon: string;
    text: string;
    textMed: string;
};

const TONE_RED: Tone = {
    bg: 'var(--cpm-shortfall-bg)',
    border: 'var(--cpm-shortfall-border)',
    icon: 'var(--cpm-shortfall-icon)',
    text: 'var(--cpm-shortfall-text)',
    textMed: 'var(--cpm-shortfall-text-med)',
};

const TONE_AMBER: Tone = {
    bg: 'var(--cpm-warning-bg)',
    border: 'var(--cpm-warning-border)',
    icon: 'var(--cpm-warning-icon)',
    text: 'var(--cpm-warning-text)',
    textMed: 'var(--cpm-warning-text-med)',
};

function toneFor(type: string): Tone {
    if (
        type === CPM_ATTENTION_TYPE.BATCH_JOB_FAILED ||
        type === CPM_ATTENTION_TYPE.BARCODE_CONFLICTS
    ) {
        return TONE_RED;
    }
    return TONE_AMBER;
}

function AttentionIcon({ type }: { type: string }) {
    const common = {
        width: 19,
        height: 19,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2,
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
    };
    if (type === CPM_ATTENTION_TYPE.BATCH_JOB_STALE) {
        return (
            <svg {...common}>
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
            </svg>
        );
    }
    if (type === CPM_ATTENTION_TYPE.STATE_NOT_CURATED) {
        return (
            <svg {...common}>
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0" />
                <circle cx="12" cy="10" r="3" />
            </svg>
        );
    }
    if (type === CPM_ATTENTION_TYPE.BARCODE_CONFLICTS) {
        return (
            <svg {...common}>
                <path d="M3 5v14" />
                <path d="M8 5v14" />
                <path d="M12 5v14" />
                <path d="M17 5v14" />
                <path d="M21 5v14" />
            </svg>
        );
    }
    if (type === CPM_ATTENTION_TYPE.REVIEW_BACKLOG) {
        return (
            <svg {...common}>
                <path d="m12 2 9 5-9 5-9-5z" />
                <path d="m3 12 9 5 9-5" />
                <path d="m3 17 9 5 9-5" />
            </svg>
        );
    }
    return (
        <svg {...common}>
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
        </svg>
    );
}

interface AttentionStripProps {
    items: CpmNeedsAttentionItem[];
    totalCount: number;
    expanded: boolean;
    onToggleExpand: () => void;
}

const COLLAPSED_CAP = 4;

export default function AttentionStrip({
    items,
    totalCount,
    expanded,
    onToggleExpand,
}: AttentionStripProps) {
    if (totalCount <= 0 || items.length === 0) {
        return (
            <div
                className="flex items-start rounded-[16px] bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)]"
                style={{ gap: 14, padding: '18px 20px' }}
            >
                <span
                    className="shrink-0 flex items-center justify-center bg-white text-[var(--cpm-success-text-light)]"
                    style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        border: '1px solid var(--cpm-success-border)',
                    }}
                >
                    <svg
                        width="19"
                        height="19"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M20 6 9 17l-5-5" />
                    </svg>
                </span>
                <div className="flex flex-col min-w-0" style={{ gap: 4 }}>
                    <div
                        className="text-[var(--cpm-success-text-light)]"
                        style={{ fontSize: 14, fontWeight: 800 }}
                    >
                        Nothing needs your attention
                    </div>
                    <div
                        style={{
                            fontSize: 13,
                            fontWeight: 500,
                            lineHeight: 1.5,
                            color: 'var(--cpm-success-text-med, var(--cpm-text-secondary))',
                        }}
                    >
                        No failed runs, barcode conflicts, or review backlog items are waiting right
                        now.
                    </div>
                </div>
            </div>
        );
    }

    const visible = totalCount <= COLLAPSED_CAP || expanded ? items : items.slice(0, COLLAPSED_CAP);
    const hidden = Math.max(0, items.length - visible.length);

    return (
        <div className="flex flex-col" style={{ gap: 12 }}>
            <div className="flex items-center" style={{ gap: 10 }}>
                <span
                    className="uppercase text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                >
                    Needs your attention
                </span>
                <span className="flex-1 h-px bg-[var(--cpm-border-default)]" />
                <span
                    className="text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 12, fontWeight: 700 }}
                >
                    {totalCount === 1 ? '1 item' : `${formatCpmCount(totalCount)} items`}
                </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 16 }}>
                {visible.map((card, idx) => {
                    const tone = toneFor(card.type);
                    const href = attentionHref(card.link);
                    const action = attentionActionLabel(card.type);
                    const isHash = href.startsWith('#');
                    const body =
                        card.detail?.trim() || 'Open the related screen to clear this item.';
                    const linkClass = '!text-[13px] !font-extrabold no-underline hover:underline';
                    return (
                        <div
                            key={`${card.type}-${idx}`}
                            className="flex items-start rounded-[16px]"
                            style={{
                                gap: 14,
                                padding: '18px 20px',
                                background: tone.bg,
                                border: `1px solid ${tone.border}`,
                            }}
                        >
                            <span
                                className="shrink-0 flex items-center justify-center bg-white"
                                style={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 10,
                                    border: `1px solid ${tone.border}`,
                                    color: tone.icon,
                                }}
                            >
                                <AttentionIcon type={card.type} />
                            </span>
                            <div className="flex flex-col min-w-0" style={{ gap: 6 }}>
                                <div style={{ fontSize: 14, fontWeight: 800, color: tone.text }}>
                                    {card.title}
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 500,
                                        lineHeight: 1.5,
                                        color: tone.textMed,
                                    }}
                                >
                                    {body}
                                </div>
                                {isHash ? (
                                    <a
                                        href={href}
                                        className={linkClass}
                                        style={{ color: tone.textMed }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            document
                                                .getElementById(href.slice(1))
                                                ?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                    >
                                        {action}
                                    </a>
                                ) : (
                                    <Link
                                        to={href}
                                        className={linkClass}
                                        style={{ color: tone.textMed }}
                                    >
                                        {action}
                                    </Link>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {items.length > COLLAPSED_CAP ? (
                <button
                    type="button"
                    onClick={onToggleExpand}
                    className="self-start inline-flex items-center rounded-full bg-white border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-brand-navy)] hover:border-[var(--cpm-brand-purple)] hover:bg-[var(--cpm-surface-subtle)]"
                    style={{ gap: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 800 }}
                >
                    {expanded ? 'Show fewer' : `Show ${hidden} more`}
                </button>
            ) : null}
        </div>
    );
}
